import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { EmploymentHistory, EmploymentHistoryFormGroup } from '../../shared/interfaces/employment-history';
import {
    Customer,
    CustomerFormGroup, CustomerFormOptions,
    CustomersFormGroup,
} from '../../shared/interfaces/customer';
import { Address, AddressFormGroup } from '../../shared/interfaces/address';
import { CustomersFormAdapterBuilderService } from './customers-form-adapter-builder.service';
import { PurlParamsService } from './purl-params.service';
import { EnhancedFormGroup } from '../../shared/classes/enhanced-formgroup';
import { CustomersFormGroupAdapters } from '../../shared/classes/form-adapter-base';
import { Sale } from '../../shared/interfaces/sale';
import { PaymentType } from '../../shared/enums/payment-type';
import { CustomerFormVariantEnum, CustomerFormVariantType } from '../../shared/interfaces/form';

@Injectable({
    providedIn: 'root',
})
export class CustomerFormsService {

    public constructor(
        private readonly forms: FormBuilder,
        private readonly adapters: CustomersFormAdapterBuilderService,
        private readonly purlParams: PurlParamsService,
    ) {}

    public createAddressFormGroup(): AddressFormGroup {
        return this.forms.group({
            unitNumber: new FormControl<string | null>(''),
            street1: new FormControl<string | null>(''),
            street2: new FormControl<string | null>(''),
            city: new FormControl<string | null>(''),
            state: new FormControl<string | null>(''),
            zip: new FormControl<string | null>(''),
            country: new FormControl<string | null>(''),
            addressType: new FormControl<'rental' | 'mortgage' | string | null>(null),
            mortgageAmount: new FormControl<number | null>(null),
            rentalAmount: new FormControl<number | null>(null),
            startDate: new FormControl<string | null>(''),
            endDate: new FormControl<string | null>(''),
            isCurrent: new FormControl<boolean | null>(true),
        });
    }

    public createCustomerFormGroup(): CustomerFormGroup {
        return this.forms.group({
            firstName: new FormControl<string | null>(''),
            middleName: new FormControl<string | null>(''),
            lastName: new FormControl<string | null>('', [Validators.required]),
            email: new FormControl<string | null>(''),
            phone: new FormControl<string | null>(''),
            dateOfBirth: new FormControl<string | null>(''),
            addresses: this.forms.array<AddressFormGroup>([]),
            employment_histories: this.forms.array<EmploymentHistoryFormGroup>([]),
        });
    }

    // Root Customers Form Group
    public createCustomersFormGroup(
        sale: Sale,
        variant?: CustomerFormVariantType,
        options?: CustomerFormOptions,
    ): EnhancedFormGroup<CustomersFormGroup> {
        const customersFormGroup = new EnhancedFormGroup<CustomersFormGroup>(
            { customers: this.forms.array<CustomerFormGroup>([]) },
        );

        switch(variant) {
            case CustomerFormVariantEnum.SINGLE_BUYER:
                this.initSingleBuyerFormGroup(customersFormGroup, sale, options);
                break;
            case CustomerFormVariantEnum.CO_BUYER:
                this.initCoBuyerFormGroup(customersFormGroup, sale, options);
                break;
            case CustomerFormVariantEnum.DEMO_SINGLE_BUYER:
                this.initDemoSingleBuyerFormGroup(customersFormGroup, sale, options);
                break;
            default: // Defaults to single buyer
                this.initSingleBuyerFormGroup(customersFormGroup, sale, options);
                break;
        }

        return customersFormGroup;
    }

    public createEmploymentHistoryFormGroup(): EmploymentHistoryFormGroup {
        return this.forms.group({
            name: new FormControl<string | null>(''),
            position: new FormControl<string | null>(''),
            startDate: new FormControl<string | null>(''),
            endDate: new FormControl<string | null>(''),
            isCurrent: new FormControl<boolean | null>(true),
            address: this.createAddressFormGroup(),
        });
    }

    private addAddress(addressFormArray: FormArray<AddressFormGroup>, count = 2): void {
        for (let i = 0; i < count; i++) {
            addressFormArray.push(this.createAddressFormGroup());
        }
    }

    private addCustomer(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        addressCount = 2,
        employmentHistoryCount = 2,
    ): void {
        (customersFormGroup.controls['customers'] as FormArray).push(this.createCustomerFormGroup());

        // Get the last customer form group added
        const lastCustomerIndex = (customersFormGroup.controls['customers'] as FormArray).length - 1;
        const lastCustomerGroup
            = (customersFormGroup.controls['customers'] as FormArray).at(lastCustomerIndex) as CustomerFormGroup;

        // Attach employment histories
        this.addEmploymentHistory(
            lastCustomerGroup.controls['employment_histories'] as FormArray,
            employmentHistoryCount,
        );

        // Attach addresses
        this.addAddress(lastCustomerGroup.controls['addresses'] as FormArray, addressCount);
    }

    private addEmploymentHistory(employmentHistoryArray: FormArray<EmploymentHistoryFormGroup>, count = 2): void {
        for (let i = 0; i < count; i++) {
            employmentHistoryArray.push(this.createEmploymentHistoryFormGroup());
        }
    }

    private buildAdapters(
        form: EnhancedFormGroup<CustomersFormGroup>,
        paymentType: PaymentType,
        type: CustomerFormVariantType,
        options?: CustomerFormOptions,
    ): CustomersFormGroupAdapters {
        return this.adapters.build(
            form,
            paymentType,
            type,
            options,
        );
    }

    private initCoBuyerFormGroup(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        sale: Sale,
        options?: CustomerFormOptions,
    ): void {
        for (let i = 0; i < 2; i++) {
            this.addCustomer(customersFormGroup, 2, 2);
            const form = (customersFormGroup.controls['customers'] as FormArray<CustomerFormGroup>).at(i);
            if (i === 0) {
                this.patchCustomerFromPurl(form);
            }
            this.patchCustomer(form, sale.customers?.[i]);
        }
        const adapters = this.buildAdapters(
            customersFormGroup,
            sale.payment_type,
            CustomerFormVariantEnum.CO_BUYER,
            options,
        );
        adapters?.forEach(a => {
            customersFormGroup.registerAdapter(a);
        });
    }

    private initDemoSingleBuyerFormGroup(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        sale: Sale,
        options?: CustomerFormOptions,
    ): void {
        this.addCustomer(customersFormGroup, 2, 2);
        this.patchCustomerDemo(
            (customersFormGroup.controls['customers'] as FormArray<CustomerFormGroup>).at(0),
        );
        const adapters = this.buildAdapters(
            customersFormGroup,
            sale.payment_type,
            CustomerFormVariantEnum.SINGLE_BUYER,
            options,
        );
        adapters?.forEach(a => {
            customersFormGroup.registerAdapter(a);
        });
    }

    private initSingleBuyerFormGroup(
        customersFormGroup: EnhancedFormGroup<CustomersFormGroup>,
        sale: Sale,
        options?: CustomerFormOptions,
    ): void {
        this.addCustomer(customersFormGroup, 2, 2);
        const form = (customersFormGroup.controls['customers'] as FormArray<CustomerFormGroup>).at(0);
        this.patchCustomerFromPurl(form);
        this.patchCustomer(form, sale?.customers?.[0]);

        const adapters = this.buildAdapters(
            customersFormGroup,
            sale.payment_type,
            CustomerFormVariantEnum.SINGLE_BUYER,
            options,
        );
        adapters?.forEach(a => {
            customersFormGroup.registerAdapter(a);
        });
    }

    private patchCustomer(form: CustomerFormGroup, customer?: Customer): void {
        if (!customer) {
            return;
        }
        form.patchValue(customer);
    }

    private patchCustomerDemo(form: CustomerFormGroup): void {
        // Create demo customer data
        const demoCustomer: Customer = {
            email: 'demo@email.com',
            firstName: 'Demo',
            middleName: '',
            lastName: 'User',
            phone: '123-456-7890',
            dateOfBirth: '2000-01-01',
            addresses: [
                {
                    unitNumber: '101',
                    street1: 'Main St',
                    street2: '',
                    city: 'Demo City',
                    state: 'Demo State',
                    zip: '12345',
                    country: 'USA',
                    addressType: 'mortgage',
                    mortgageAmount: 2450,
                    rentalAmount: undefined,
                    startDate: '2020-07-08',
                    endDate: '',
                    isCurrent: true,
                } as Address,
            ],
            employment_histories: [
                {
                    name: 'Demo Company',
                    position: 'Software Developer',
                    startDate: '2021-01-01',
                    endDate: '',
                    isCurrent: true,
                    address: {
                        unitNumber: '202',
                        street1: 'Work St',
                        street2: '',
                        city: 'Work City',
                        state: 'Work State',
                        zip: '54321',
                        country: 'USA',
                        addressType: 'office',
                        mortgageAmount: undefined,
                        rentalAmount: undefined,
                        startDate: '2021-01-01',
                        endDate: '',
                        isCurrent: true,
                    } as Address,
                } as EmploymentHistory,
            ],
        } as Customer;

        form.patchValue(demoCustomer);
    }

    private patchCustomerFromPurl(form: CustomerFormGroup): void {
        const params = this.purlParams.getParams();
        if (Object.keys(params).length) {
            form.patchValue(params);
        }
    }
}
